/**
 * Reading the `type` deep-link param, which locks the wizard to one
 * registration role.
 *
 * Unlike `division_id` and `team_id` — which pre-select once and then get out
 * of the way — this param is a standing constraint: while it is in the URL the
 * other role stays disabled, so a link built for players cannot be walked into
 * a team registration.
 */

import {
  REGISTRATION_ROLE_PLAYER,
  REGISTRATION_ROLE_TEAM,
} from '@sportninja/common/features/registration/registrationUrlParams'

/** Which role a link locks to. */
export type LockedRole = 'player' | 'team'

/**
 * Read the `type` param as a role, or null when it says nothing usable.
 *
 * Unknown values are null rather than an error: the param is hand-editable and
 * appears in shared links, so a typo must degrade to the ordinary "pick your
 * role" screen instead of locking the reader out of both options.
 */
export const lockedRoleFromParam = (
  value: string | null | undefined
): LockedRole | null => {
  const normalized = value?.trim().toLowerCase()
  if (normalized === REGISTRATION_ROLE_PLAYER) {
    return 'player'
  }
  if (normalized === REGISTRATION_ROLE_TEAM) {
    return 'team'
  }
  return null
}

interface Availability {
  /** The division accepts player registrations. */
  playerRegistrationEnabled?: boolean
  /** The division accepts team registrations. */
  teamRegistrationEnabled?: boolean
  /** Team registration is closed because the division has no spots left. */
  teamRegistrationFull?: boolean
}

interface LinkedRoleApplication {
  /** The role the link names, or null when it names none this step can offer. */
  linkedRole: LockedRole | null
  /** The registration type id that role resolves to, once the types load. */
  linkedTypeId?: string
  /** The role currently held, from context or a restored session. */
  currentRoleId?: string
  /** Whether this link has already been honoured once on this screen. */
  alreadyApplied: boolean
}

/**
 * Whether the link's role should be written into context right now.
 *
 * The link has to CORRECT, not merely fill an empty field: a resumed session
 * restores its own `registration_type_id`, which can disagree with the link the
 * reader just followed, and the link is the fresher choice.
 *
 * But "the session disagrees" and "the reader just pressed the other card" look
 * identical — both are only a role id that differs from the link's. Deciding on
 * that alone is what made the role unchangeable: every manual switch was read as
 * a stale session and reverted, and the chosen player was cleared with it.
 *
 * `alreadyApplied` is what separates them. The correction is owed once per link,
 * on arrival; after that the reader's own choice stands, and the URL follows
 * them instead of the other way round.
 */
export const shouldApplyLinkedRole = ({
  linkedRole,
  linkedTypeId,
  currentRoleId,
  alreadyApplied,
}: LinkedRoleApplication): boolean => {
  // Nothing to apply, or the registration types have not loaded yet — matching
  // on an undefined id would read as a conflict and clear a legitimate role.
  if (!linkedRole || !linkedTypeId) {
    return false
  }
  if (alreadyApplied) {
    return false
  }
  return currentRoleId !== linkedTypeId
}

/**
 * Whether a locked role can actually be honoured on this division.
 *
 * A link can outlive the thing it points at — registration types get switched
 * off, divisions fill up. Locking to a role the division no longer offers would
 * leave the reader staring at two dead cards, so the param is dropped instead
 * and they choose normally.
 */
export const isLockedRoleAvailable = (
  role: LockedRole | null,
  {
    playerRegistrationEnabled,
    teamRegistrationEnabled,
    teamRegistrationFull,
  }: Availability
): boolean => {
  if (role === 'player') {
    return Boolean(playerRegistrationEnabled)
  }
  if (role === 'team') {
    return Boolean(teamRegistrationEnabled) && !teamRegistrationFull
  }
  return false
}
